import { useState, useRef, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  X, Download, Square, MousePointer2, Send, Trash2,
  Loader2, Undo2, Check, Image as ImageIcon, Play, ChevronDown, Plus,
  Volume2, VolumeX, BadgeCheck,
} from "lucide-react";
import { ImageActionMenu } from "./ImageActionMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OverlayCanvas, ShapeOverlay, flattenOverlaysToCanvas } from "./CreativeToolkit";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { downloadUrlAsFile } from "@/lib/download";
import { useVideoVersions } from "@/hooks/useVideoVersions";
import { VideoToolbarControls, VideoPlayerOverlay, VideoPromptDialog } from "./VideoControls";
import { motion, AnimatePresence } from "framer-motion";
import { supabase, getAuthHeader } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";

interface Annotation {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  comment: string;
}

const PRESET_ISSUES = [
  { key: "product_incorrect", label: "Product doesn't look correct" },
  { key: "wrong_placement", label: "Wrong placement / position" },
  { key: "wrong_scale", label: "Wrong scale / proportions" },
  { key: "wrong_color", label: "Wrong color / material" },
  { key: "lighting_mismatch", label: "Lighting doesn't match scene" },
  { key: "warm_airflow", label: "Add warm airflow glow from the unit's air outlet, a soft golden radiance spreading outward suggesting heat" },
] as const;

type PresetIssueKey = (typeof PRESET_ISSUES)[number]["key"];

interface FormatEditLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  productImageUrls?: string[];
  onDelete?: () => void;
  onImageEdited?: (newUrl: string) => void;
  onOverlaysChanged?: (overlays: ShapeOverlay[]) => void;
  formatRatio?: string;
  formatWidth?: number;
  formatHeight?: number;
  workspaceId?: string;
  userId?: string;
  variantId?: string;
  initialOverlays?: ShapeOverlay[];
}

export function FormatEditLightbox({
  isOpen,
  onClose,
  imageUrl,
  productImageUrls,
  onDelete,
  onImageEdited,
  onOverlaysChanged,
  formatRatio,
  formatWidth,
  formatHeight,
  workspaceId,
  userId,
  variantId,
  initialOverlays,
}: FormatEditLightboxProps) {
  const [currentImage, setCurrentImage] = useState(imageUrl);
  const [previousImage, setPreviousImage] = useState<string | null>(null);

  // Track the last prop we synced from, to detect external changes
  const lastSyncedPropRef = useRef(imageUrl);

  // Reset when imageUrl prop changes externally (not from our own edits)
  useEffect(() => {
    if (imageUrl !== lastSyncedPropRef.current) {
      lastSyncedPropRef.current = imageUrl;
      // Only reset if this is a genuinely different image (not our own edit callback)
      if (imageUrl !== currentImage && imageUrl !== previousImage) {
        setCurrentImage(imageUrl);
        setPreviousImage(null);
      }
    }
  }, [imageUrl]);

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [currentRect, setCurrentRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState(true);
  const [selectedIssues, setSelectedIssues] = useState<Set<PresetIssueKey>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAnnotations, setProcessingAnnotations] = useState<Annotation[]>([]);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Creative toolkit state
  const [overlays, setOverlays] = useState<ShapeOverlay[]>(initialOverlays || []);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);


  // Visible AI-disclosure watermark: defaults to the workspace setting, with a
  // per-export override in the actions menu. (The machine-readable XMP mark is
  // always written and is not affected by this.)
  const video = useVideoVersions({
    table: "deliverables",
    rowId: variantId,
    isOpen,
    imageUrl: currentImage,
    workspaceId,
    userId,
    formatRatio,
    formatWidth,
    formatHeight,
  });
  const isPlaying = video.isPlaying;

  const { workspace } = useWorkspace();
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  useEffect(() => {
    setWatermarkEnabled(workspace?.ai_watermark_enabled ?? true);
  }, [workspace?.ai_watermark_enabled, isOpen]);




  // Load overlays from DB when opening
  useEffect(() => {
    if (!isOpen || !variantId) return;
    if (initialOverlays && initialOverlays.length > 0) {
      setOverlays(initialOverlays);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("deliverables")
        .select("overlays")
        .eq("id", variantId)
        .single();
      if (!error && data) {
        if (data.overlays && Array.isArray(data.overlays)) {
          setOverlays(data.overlays as unknown as ShapeOverlay[]);
        }
      }
    })();
  }, [isOpen, variantId]);

  // Auto-save overlays to DB when they change
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!variantId || !isOpen) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      await supabase
        .from("deliverables")
        .update({ overlays: overlays as any } as any)
        .eq("id", variantId);
      onOverlaysChanged?.(overlays);
    }, 800);
    return () => clearTimeout(saveTimeoutRef.current);
  }, [overlays, variantId, isOpen]);

  // Escape to delete selected annotation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (selectedOverlayId) {
          setSelectedOverlayId(null);
        } else if (selectedId) {
          e.stopPropagation();
          deleteAnnotation(selectedId);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedId, selectedOverlayId]);

  const toggleIssue = (key: PresetIssueKey) => {
    setSelectedIssues((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getNormalizedCoords = useCallback((e: React.MouseEvent) => {
    const rect = imgContainerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!drawMode || isProcessing || isPlaying) return;
    // Don't start drawing if clicking on an overlay
    if ((e.target as HTMLElement).closest('[data-overlay]')) return;
    const coords = getNormalizedCoords(e);
    if (!coords) return;
    setIsDrawing(true);
    setDrawStart(coords);
    setCurrentRect({ x: coords.x, y: coords.y, w: 0, h: 0 });
  }, [drawMode, isProcessing, isPlaying, getNormalizedCoords]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDrawing || !drawStart) return;
    const coords = getNormalizedCoords(e);
    if (!coords) return;
    setCurrentRect({
      x: Math.min(drawStart.x, coords.x),
      y: Math.min(drawStart.y, coords.y),
      w: Math.abs(coords.x - drawStart.x),
      h: Math.abs(coords.y - drawStart.y),
    });
  }, [isDrawing, drawStart, getNormalizedCoords]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !currentRect) { setIsDrawing(false); return; }
    if (currentRect.w > 0.02 && currentRect.h > 0.02) {
      const newAnn: Annotation = {
        id: crypto.randomUUID(),
        x: currentRect.x, y: currentRect.y,
        width: currentRect.w, height: currentRect.h,
        comment: "",
      };
      setAnnotations((prev) => [...prev, newAnn]);
      setSelectedId(newAnn.id);
    }
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentRect(null);
  }, [isDrawing, currentRect]);

  const updateComment = (id: string, comment: string) => {
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, comment } : a)));
  };

  const deleteAnnotation = (id: string) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const handleUndo = () => {
    if (!previousImage) return;
    setCurrentImage(previousImage);
    setPreviousImage(null);
    onImageEdited?.(previousImage);
  };

  const handleSubmit = async () => {
    const withComments = annotations.filter((a) => a.comment.trim());
    if (withComments.length === 0 && selectedIssues.size === 0) return;

    setProcessingAnnotations(withComments);
    const savedAnnotations = [...withComments];
    setAnnotations([]);
    setSelectedId(null);
    setIsProcessing(true);

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/edit-render`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authHeader,
          },
          body: JSON.stringify({
            imageUrl: currentImage,
            annotations: savedAnnotations,
            selectedIssues: Array.from(selectedIssues),
            productImageUrls: productImageUrls || [],
            workspace_id: workspaceId,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Edit failed");
      }

      const data = await response.json();
      setPreviousImage(currentImage);
      setCurrentImage(data.image);
      lastSyncedPropRef.current = data.image; // prevent useEffect from overwriting
      onImageEdited?.(data.image);
      setSelectedIssues(new Set());
      setDrawMode(true);
    } catch (error: any) {
      console.error("Edit error:", error);
    } finally {
      setIsProcessing(false);
      setProcessingAnnotations([]);
    }
  };





  const handleUpdateOverlay = useCallback((id: string, patch: Partial<ShapeOverlay>) => {
    setOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  }, []);

  // Image overlay upload
  const addImageFromUrl = useCallback((url: string) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const containerW = imgContainerRef.current?.offsetWidth || 400;
      const initialW = containerW * 0.25;
      const ar = (img.naturalWidth || 200) / (img.naturalHeight || 200);
      const newOverlay: ShapeOverlay = {
        id: crypto.randomUUID(),
        type: "image",
        x: 0.5,
        y: 0.5,
        widthPx: initialW,
        heightPx: initialW / ar,
        aspectRatio: ar,
        opacity: 1,
        imageSrc: url,
      };
      setOverlays((prev) => [...prev, newOverlay]);
      setSelectedOverlayId(newOverlay.id);
    };
    img.src = url;
  }, []);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (workspaceId && userId) {
      const ext = file.name.split(".").pop() || "png";
      const storagePath = `${userId}/${workspaceId}/${crypto.randomUUID()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from("overlays").upload(storagePath, file, { contentType: file.type });
      if (uploadErr) { return; }
      const { data: urlData } = supabase.storage.from("overlays").getPublicUrl(storagePath);
      await supabase.from("overlay_assets").insert({ workspace_id: workspaceId, user_id: userId, file_name: file.name, storage_path: storagePath, mime_type: file.type } as any);
      addImageFromUrl(urlData.publicUrl);
    } else {
      addImageFromUrl(URL.createObjectURL(file));
    }
  };

  const deleteOverlay = (id: string) => {
    setOverlays((prev) => prev.filter((o) => o.id !== id));
    if (selectedOverlayId === id) setSelectedOverlayId(null);
  };

  // Download with overlays flattened, plus AI-provenance marking.
  //
  // Every export carries machine-readable XMP (IPTC DigitalSourceType =
  // trainedAlgorithmicMedia) — required by EU AI Act Art. 50(2) and not
  // user-toggleable. The visible bottom-right label is controlled by
  // `watermarkEnabled` (workspace default, overridable per export).
  const handleDownload = useCallback(async () => {
    const filename = `format-${formatRatio?.replace(":", "x") || "edit"}-${Date.now()}.png`;
    // downloadUrlAsFile applies provenance marking centrally; we only pass the
    // per-export watermark override.
    let source = currentImage;
    if (overlays.length > 0 && imgContainerRef.current) {
      try {
        source = await flattenOverlaysToCanvas(overlays, imgContainerRef.current, currentImage);
      } catch {
        // fall through to the unflattened image
      }
    }
    await downloadUrlAsFile(source, filename, { watermark: watermarkEnabled });
  }, [currentImage, overlays, formatRatio, watermarkEnabled]);

  const annotationsWithComments = annotations.filter((a) => a.comment.trim());
  const canSubmit = annotations.length > 0 || selectedIssues.size > 0;
  const hasOverlays = overlays.length > 0;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-background/95 backdrop-blur-lg border-border">
        <VisuallyHidden><DialogTitle>Edit Format Variant</DialogTitle></VisuallyHidden>

        <div className="flex h-[90vh]">
          {/* Main image area */}
          <div className="flex-1 flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <div className="flex items-center gap-2">
                <VideoToolbarControls video={video} />


                <ImageActionMenu
                  toggles={[
                    {
                      label: "AI watermark",
                      icon: <BadgeCheck className="w-4 h-4" />,
                      checked: watermarkEnabled,
                      onCheckedChange: setWatermarkEnabled,
                      hint: "Visible label on download",
                    },
                  ]}
                  actions={[
                    { label: "Add Image", icon: <ImageIcon className="w-4 h-4" />, onClick: () => fileInputRef.current?.click() },
                    ...(previousImage ? [{ label: "Undo", icon: <Undo2 className="w-4 h-4" />, onClick: handleUndo, disabled: isProcessing }] : []),
                    { label: "Download", icon: <Download className="w-4 h-4" />, onClick: handleDownload },
                    ...(onDelete ? [{ label: "Delete", icon: <Trash2 className="w-4 h-4" />, onClick: () => { onClose(); onDelete(); }, destructive: true }] : []),
                  ]}
                />
                {formatRatio && (
                  <span className="text-xs text-muted-foreground ml-2">
                    {formatRatio} · {formatWidth}×{formatHeight}
                  </span>
                )}
              </div>
            </div>

            {/* Canvas */}
            <div className="flex-1 flex items-center justify-center p-4 overflow-auto" onClick={() => {
              if (selectedId && !drawMode) setSelectedId(null);
              if (selectedOverlayId) setSelectedOverlayId(null);
            }}>
              <div
                ref={imgContainerRef}
                className={cn(
                  "relative inline-block max-w-full select-none",
                  drawMode && !isProcessing && !hasOverlays && !isPlaying && "cursor-crosshair",
                )}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={(e) => handleMouseUp()}
                onMouseLeave={() => { if (isDrawing) handleMouseUp(); }}
              >
                <img
                  src={currentImage}
                  alt="Format variant"
                  className="max-w-full max-h-[75vh] object-contain rounded-lg"
                  crossOrigin="anonymous"
                  draggable={false}
                  style={formatWidth && formatHeight ? { aspectRatio: `${formatWidth}/${formatHeight}` } : undefined}
                />

                <VideoPlayerOverlay video={video} formatWidth={formatWidth} formatHeight={formatHeight} />

                {/* Creative overlays */}
                <div className="absolute inset-0 z-[60] pointer-events-none">
                  <div className="relative w-full h-full pointer-events-auto">
                    <OverlayCanvas
                      overlays={overlays}
                      selectedId={selectedOverlayId}
                      setSelectedId={setSelectedOverlayId}
                      setOverlays={setOverlays}
                      containerRef={imgContainerRef}
                    />
                  </div>
                </div>

                {/* Annotations — hidden during video playback */}
                {!isPlaying && !isProcessing && annotations.map((ann, i) => (
                  <div
                    key={ann.id}
                    className={cn(
                      "absolute border-2 rounded transition-colors",
                      selectedId === ann.id ? "border-primary bg-primary/10" : "border-primary/60 bg-primary/5 hover:border-primary"
                    )}
                    style={{ left: `${ann.x * 100}%`, top: `${ann.y * 100}%`, width: `${ann.width * 100}%`, height: `${ann.height * 100}%` }}
                    onClick={(e) => { e.stopPropagation(); if (!drawMode) setSelectedId(ann.id); }}
                  >
                    <div className="absolute -top-3 -left-3 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shadow-md">
                      {i + 1}
                    </div>
                  </div>
                ))}

                {/* Sparkle effect */}
                {isProcessing && processingAnnotations.map((ann) => (
                  <div
                    key={ann.id}
                    className="absolute rounded overflow-hidden pointer-events-none"
                    style={{ left: `${ann.x * 100}%`, top: `${ann.y * 100}%`, width: `${ann.width * 100}%`, height: `${ann.height * 100}%` }}
                  >
                    {Array.from({ length: 20 }).map((_, si) => {
                      const size = [6, 8, 10, 12, 14, 7, 11, 9, 13, 8, 10, 6, 14, 12, 8, 10, 7, 11, 9, 13][si];
                      const cols = 5;
                      const rows = 4;
                      const col = si % cols;
                      const row = Math.floor(si / cols);
                      const cellW = 100 / cols;
                      const cellH = 100 / rows;
                      const jitterX = ((si * 17 + 7) % 11) / 11 * cellW * 0.6;
                      const jitterY = ((si * 13 + 3) % 11) / 11 * cellH * 0.6;
                      return (
                        <motion.div
                          key={si}
                          className="absolute"
                          style={{
                            left: `${col * cellW + cellW * 0.2 + jitterX}%`,
                            top: `${row * cellH + cellH * 0.2 + jitterY}%`,
                            width: size, height: size,
                            filter: `drop-shadow(0 0 ${size / 2}px rgba(255,255,255,0.9))`,
                          }}
                          animate={{ opacity: [0, 1, 0.4, 1, 0], scale: [0.3, 1.1, 0.7, 1.2, 0.3], rotate: [0, 45, 0, -45, 0] }}
                          transition={{ duration: 2.2, repeat: Infinity, delay: si * 0.18, ease: "easeInOut" }}
                        >
                          <svg viewBox="0 0 24 24" fill="white" className="w-full h-full">
                            <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41Z" />
                          </svg>
                        </motion.div>
                      );
                    })}
                  </div>
                ))}

                {/* Drawing rect */}
                {currentRect && (
                  <div
                    className="absolute border-2 border-dashed border-primary bg-primary/10 rounded"
                    style={{ left: `${currentRect.x * 100}%`, top: `${currentRect.y * 100}%`, width: `${currentRect.w * 100}%`, height: `${currentRect.h * 100}%` }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Right panel: Overlays + Annotations */}
          <div className="w-72 border-l border-border flex flex-col bg-card">
            {/* Hidden file input */}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />

            {/* Overlay layers */}
            {overlays.length > 0 && (
              <div className="border-b border-border p-3 space-y-2">
                <h3 className="font-display font-semibold text-sm">Layers ({overlays.length})</h3>
                {overlays.map((ov) => (
                  <div
                    key={ov.id}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer transition-colors",
                      selectedOverlayId === ov.id
                        ? "bg-primary/10 border border-primary/30 text-foreground"
                        : "hover:bg-muted text-muted-foreground border border-transparent"
                    )}
                    onClick={() => setSelectedOverlayId(ov.id)}
                  >
                    <ImageIcon className="w-3 h-3 flex-shrink-0" />
                    <span className="flex-1 truncate">Image</span>
                    <button
                      className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
                      onClick={(e) => { e.stopPropagation(); deleteOverlay(ov.id); }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {selectedOverlayId && overlays.find(o => o.id === selectedOverlayId) && (
                  <div className="pt-1">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                      Opacity ({Math.round((overlays.find(o => o.id === selectedOverlayId)?.opacity || 1) * 100)}%)
                    </label>
                    <Slider
                      value={[(overlays.find(o => o.id === selectedOverlayId)?.opacity || 1) * 100]}
                      onValueChange={([v]) => handleUpdateOverlay(selectedOverlayId, { opacity: v / 100 })}
                      min={10}
                      max={100}
                      step={5}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Annotations section */}
            {annotations.length > 0 && (
              <>
                <div className="p-3 border-t border-border">
                  <h3 className="font-display font-semibold text-sm">Annotations ({annotations.length})</h3>
                  <p className="text-xs text-muted-foreground mt-1">Describe what to change</p>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  <AnimatePresence>
                    {annotations.map((ann, i) => (
                      <motion.div
                        key={ann.id}
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 20 }}
                        className={cn("rounded-lg border p-2.5 transition-colors", selectedId === ann.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}
                        onClick={() => setSelectedId(ann.id)}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">{i + 1}</div>
                            <span className="text-xs text-muted-foreground">Region {i + 1}</span>
                          </div>
                          <Button variant="ghost" size="icon" className="w-5 h-5" onClick={(e) => { e.stopPropagation(); deleteAnnotation(ann.id); }} disabled={isProcessing}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <Textarea
                          placeholder="Describe what to change..."
                          value={ann.comment}
                          onChange={(e) => updateComment(ann.id, e.target.value)}
                          className="min-h-[50px] text-xs resize-none"
                          disabled={isProcessing}
                          autoFocus={selectedId === ann.id && !ann.comment}
                        />
                        {selectedId === ann.id && (
                          <div className="mt-2">
                            <p className="text-[10px] text-muted-foreground mb-1.5">Quick suggestions:</p>
                            <div className="flex flex-wrap gap-1.5">
                              {PRESET_ISSUES.map((issue) => (
                                <button
                                  key={issue.key}
                                  type="button"
                                  disabled={isProcessing}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const current = ann.comment.trim();
                                    if (current.includes(issue.label)) return;
                                    updateComment(ann.id, current ? `${current}. ${issue.label}` : issue.label);
                                  }}
                                  className={cn(
                                    "px-2 py-1 rounded-md text-[10px] border transition-colors",
                                    ann.comment.includes(issue.label)
                                      ? "bg-primary/15 border-primary/30 text-primary"
                                      : "bg-secondary border-transparent hover:border-primary/30 text-muted-foreground hover:text-foreground"
                                  )}
                                >
                                  {issue.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                <div className="p-3 border-t border-border">
                  <Button
                    className="w-full gradient-primary text-primary-foreground"
                    onClick={handleSubmit}
                    disabled={isProcessing || !canSubmit}
                  >
                    {isProcessing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                    Apply Edits
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <VideoPromptDialog video={video} />
    </>
  );
}
